import React, { useEffect, useRef } from 'react';
import { useTheme } from 'styled-components';
import { IconClose } from '../../app/icons/close';
import { Loading } from '../../app/loading/loading';
import { ModalAlerts } from '../alerts/modal/alerts';

type ModalType = {
    children: React.ReactNode;
    show?: boolean;
    title?: React.ReactNode | string;
    icon?: React.ReactNode;
    footer?: React.ReactNode;
    onClose?: () => void;
    loading?: boolean;
    width?: number;
    showCloseButton?: boolean;
};

export const Modal: React.FC<ModalType> = ({
    show,
    title,
    icon,
    children,
    onClose,
    footer,
    loading = false,
    width,
    showCloseButton = true,
}) => {
    const theme = useTheme();
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!show) return undefined;
        const previouslyFocused = document.activeElement as HTMLElement | null;
        const focusTarget =
            contentRef.current?.querySelector<HTMLElement>('input:not([disabled])') ??
            contentRef.current?.querySelector<HTMLElement>('button, a[href]');
        focusTarget?.focus();
        return () => previouslyFocused?.focus();
    }, [show]);

    useEffect(() => {
        if (show) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
    }, [show]);

    return show ? (
        <div className="fixed inset-0 z-[1000] p-[1rem] backdrop-blur-[0.5rem] md:flex md:items-center md:justify-center md:p-[2rem]">
            <div
                ref={contentRef}
                className="relative z-[1200] max-h-full w-full overflow-auto rounded-medium p-[2rem] md:px-[6.4rem] md:pb-[6.4rem] md:pt-[3.2rem]"
                style={{
                    backgroundColor: '#ffffff',
                    maxWidth: `${width ?? 62}rem`,
                }}
            >
                <Loading active={loading}>
                    <div className="flex flex-col items-center pt-medium">
                        {icon}
                        <div className="mb-medium mt-small text-center text-xlarge leading-xlarge font-bold text-brand-secondary">
                            {title}
                        </div>
                    </div>
                    <ModalAlerts />
                    {children}
                    {footer && (
                        <div className="mt-medium flex items-center justify-between [&>*:only-child]:ml-auto">
                            {footer}
                        </div>
                    )}
                </Loading>
                {showCloseButton && (
                    <button
                        type="button"
                        className="absolute right-[1.5rem] top-[1.5rem] cursor-pointer border-0 bg-transparent p-0 [&_i]:h-[3rem] [&_i]:w-[3rem] [&_i]:text-[var(--modal-close-color)] hover:[&_i]:text-[var(--modal-close-hover-color)]"
                        onClick={onClose}
                        style={{
                            transition: `color ${theme.base.transition}`,
                            ['--modal-close-color' as string]: theme.modal.close.base.color,
                            ['--modal-close-hover-color' as string]: theme.modal.close.hover.color,
                        }}
                    >
                        <IconClose />
                    </button>
                )}
            </div>

            {showCloseButton ? (
                <div
                    className="fixed inset-0 z-[1100]"
                    onClick={onClose}
                    style={{ backgroundColor: 'rgb(114 100 90 / 45%)' }}
                />
            ) : (
                <div className="fixed inset-0 z-[1100]" style={{ backgroundColor: 'rgb(114 100 90 / 45%)' }} />
            )}
        </div>
    ) : null;
};
